import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useDropzone } from "react-dropzone";
import Layout from "../../../componentes/Comunes/Layout/Layout";
import ServicioSolicitudPadron from "../../../servicios/SolicitudDePadron";
import StickyTopBox from "../../../componentes/Comunes/StickyBox/StickyTopBox";
import PaisesDropdown from "../../../componentes/Comunes/Dropdowns/Paises/PaisesDropdown";
import DatePicker from "react-datepicker";
import GenerosDropdown from "../../../componentes/Comunes/Dropdowns/Generos/GenerosDropdown";
import { Notificar } from "../../../componentes/Comunes/Notificador";
import { Documento } from "../../../Contenedores/Comun/Documento";
import TipoDocumento from "../../../Contenedores/Comun/TipoDocumento";
import { format, isSameDay } from "date-fns";
import InputTextoPadron from "../../../componentes/Padron/Comunes/InputTextoPadron";
import PadronServicio from "../../../servicios/Padron";
import GuiaPasaporteModal from "../../../componentes/Padron/Comunes/GuiaPasaporteModal";

const SolicitudPasaporte = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scale, setScale] = useState(1);
  const [datosSolicitud, setDatosSolicitud] = useState(null);
  const [personaEnBD, setPersonaEnBD] = useState(null);
  const [esIdentificacionValida, setEsIdentificacionValida] = useState(true);
  const viewerRef = useRef(null);
  const existeIdentificacionInicial = useRef(false);

  const TIPO_PERSONA_PASAPORTE = 5;
  const INCLUSION = 1;
  const MODIFICACION = 2;

  const resetearEstados = () => {
    setFile(null);
    setPreview(null);
    setScale(1);
    setDatosSolicitud(null);
    setPersonaEnBD(null);
    setEsIdentificacionValida(true);
    existeIdentificacionInicial.current = false;
  };

  const parametroIngresoSolicitudPadronBase = useMemo(
    () => ({
      tipoSolicitud: INCLUSION,
      tipoPersona: TIPO_PERSONA_PASAPORTE,
      identificacionPersona: "",
      nombreORazonSocial: "",
      primerApellido: "",
      segundoApellido: "",
      fechaConstitucionONacimiento: null,
      paisRegistroONacimiento: "",
      genero: 0,
      listaDocumentos: [],
    }),
    []
  );

  const prepararDatosSolicitud = useCallback(
    (parametroIngresoSolicitudPadron, archivo) => {
      const documento = new Documento();
      documento.TipoDocumento = new TipoDocumento();
      documento.TipoDocumento.Codigo = 1;
      documento.NombreArchivo = archivo.nombreArchivo;
      documento.ArchivoBase64 = archivo.archivoCadena;

      parametroIngresoSolicitudPadron =
        parametroIngresoSolicitudPadron ?? parametroIngresoSolicitudPadronBase;

      parametroIngresoSolicitudPadron.listaDocumentos = [documento];

      parametroIngresoSolicitudPadron.fechaConstitucionONacimiento =
        parametroIngresoSolicitudPadron.fechaConstitucionONacimiento
          ? new Date(
              parametroIngresoSolicitudPadron.fechaConstitucionONacimiento
            )
          : null;

      return parametroIngresoSolicitudPadron;
    },
    [parametroIngresoSolicitudPadronBase]
  );

  // Manejo de dropzone
  const onDrop = useCallback(
    (acceptedFiles) => {
      const archivoAdjunto = acceptedFiles[0];
      if (!archivoAdjunto) return;

      const reader = new FileReader();

      reader.readAsDataURL(archivoAdjunto);

      reader.onloadend = async () => {
        const base64String = reader.result.split(",")[1];

        const archivo = {
          nombreArchivo: archivoAdjunto.name,
          archivoCadena: base64String,
        };

        const datos = await ServicioSolicitudPadron.ObtengaDatosPasaporte(
          archivo
        );

        if (!datos.esValido) {
          Notificar("error", datos.errores);
          return;
        }

        if (datos.mensaje) {
          Notificar("warn", datos.mensaje, 10000);
        }

        const parametro = prepararDatosSolicitud(
          datos.parametroIngresoSolicitudPadron,
          archivo
        );

        if (datos.personaEnPadron) {
          datos.personaEnPadron.fechaConstitucionONacimiento = new Date(
            datos.personaEnPadron.fechaConstitucionONacimiento
          );
        }

        setDatosSolicitud(parametro);
        setPersonaEnBD(datos.personaEnPadron);
        setFile(archivoAdjunto);

        existeIdentificacionInicial.current = true;

        if (
          archivoAdjunto.type.includes("image") ||
          archivoAdjunto.type.includes("pdf")
        ) {
          setPreview(URL.createObjectURL(archivoAdjunto));
        }
      };
    },
    [prepararDatosSolicitud]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/tiff": [".tif", ".tiff"],
      "image/bmp": [".bmp"],
      "application/pdf": [".pdf"],
    },
    multiple: false,
  });

  const obtenerPersona = useCallback(async (identificacionPersona) => {
    const identificacion = {
      codigo: identificacionPersona,
      tipoPersona: {
        codigo: TIPO_PERSONA_PASAPORTE,
      },
    };

    try {
      const persona = await PadronServicio.ObtengaPersonaUnificada(
        identificacion
      );

      setEsIdentificacionValida(true);

      if (persona?.fechaConstitucionONacimiento) {
        persona.fechaConstitucionONacimiento = new Date(
          persona.fechaConstitucionONacimiento
        );
      }

      if (!persona?.identificacionPrincipal) {
        modificarCampo("tipoSolicitud", INCLUSION);
        setPersonaEnBD(null);
      } else {
        modificarCampo("tipoSolicitud", MODIFICACION);
        setPersonaEnBD(persona);
      }
    } catch (error) {
      setEsIdentificacionValida(false);
    }
  }, []);

  useEffect(() => {
    if (!existeIdentificacionInicial.current) {
      return;
    }

    if (!datosSolicitud?.identificacionPersona) return;

    const timeoutId = setTimeout(async () => {
      await obtenerPersona(datosSolicitud?.identificacionPersona);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [datosSolicitud?.identificacionPersona, obtenerPersona]);

  const modificarCampo = (name, value) => {
    setDatosSolicitud((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    modificarCampo(name, value);
  };

  const cambios = {
    nombreORazonSocial:
      datosSolicitud?.tipoSolicitud === MODIFICACION &&
      datosSolicitud?.nombreORazonSocial !== personaEnBD?.nombreORazonSocial,
    primerApellido:
      datosSolicitud?.tipoSolicitud === MODIFICACION &&
      datosSolicitud?.primerApellido !== personaEnBD?.primerApellido,
    segundoApellido:
      datosSolicitud?.tipoSolicitud === MODIFICACION &&
      datosSolicitud?.segundoApellido !== personaEnBD?.segundoApellido,
    fechaConstitucionONacimiento:
      datosSolicitud?.tipoSolicitud === MODIFICACION &&
      !isSameDay(
        datosSolicitud?.fechaConstitucionONacimiento,
        personaEnBD?.fechaConstitucionONacimiento
      ),
    paisRegistroONacimiento:
      datosSolicitud?.tipoSolicitud === MODIFICACION &&
      datosSolicitud?.paisRegistroONacimiento !==
        personaEnBD?.paisRegistroONacimiento?.codigoPais,
    genero:
      datosSolicitud?.tipoSolicitud === MODIFICACION &&
      datosSolicitud?.genero?.toString() !== personaEnBD?.genero?.toString(),
  };

  const ingresarSolicitud = async () => {
    if (!esIdentificacionValida) {
      Notificar(
        "error",
        "La identificación aún no ha sido validada. Por favor haga clic en 'Validar Identificación'."
      );
      return;
    }

    const resultado = await ServicioSolicitudPadron.IngresarSolicitudDePadron(
      datosSolicitud
    );

    if (resultado.esValido) {
      resetearEstados();
      Notificar("success", resultado.mensaje);
    } else {
      Notificar("error", resultado.errores);
    }
  };

  const aplicarZoom = (nuevaEscala) => {
    setScale(nuevaEscala);

    // Cuando hace zoom mueve el scroll hacia abajo
    if (viewerRef.current) {
      viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
      viewerRef.current.scrollLeft = viewerRef.current.scrollWidth;
    }
  };

  const zoomIn = () => aplicarZoom(scale + 0.2);

  const zoomOut = () => {
    if (scale > 0.8) aplicarZoom(scale - 0.2);
  };

  const resetearZoom = () => {
    setScale(1);
    if (viewerRef.current) {
      viewerRef.current.scrollTop = 0;
      viewerRef.current.scrollLeft = 0;
    }
  };

  const renderOriginal = (campo, textoOriginal, valorOriginal) => {
    return (
      <small className="form-text text-muted">
        Original: <strong>{textoOriginal}</strong>
        {cambios[campo] && (
          <button
            type="button"
            className="badge badge-pill bg-secondary text-white border-0 ml-2"
            title="Usar Original"
            onClick={() => modificarCampo(campo, valorOriginal)}
          >
            <i className="fas fa-undo-alt"></i>
          </button>
        )}
      </small>
    );
  };

  return (
    <Layout>
      <StickyTopBox>
        <div className="row">
          <div className="col">
            <h1 className="h3 mb-0 text-gray-800">
              Registro de Solicitud de Pasaporte
            </h1>
          </div>

          {file ? (
            <div className="col text-right">
              <button
                onClick={resetearEstados}
                className="btn btn-secondary btn-icon-split mr-3"
              >
                <span className="icon text-white-50">
                  <i className="far fa-arrow-alt-circle-left"></i>
                </span>
                <span className="text">Adjuntar nuevo archivo</span>
              </button>

              <button
                onClick={ingresarSolicitud}
                className="btn btn-primary btn-icon-split mr-3"
              >
                <span className="icon text-white-50">
                  <i className="far fa-check-circle"></i>
                </span>
                <span className="text">Ingresar Solicitud</span>
              </button>
            </div>
          ) : null}
        </div>
      </StickyTopBox>

      <div className="card shadow mb-4">
        <div className="card-body text-gray-800">
          <div className="container-fluid">
            {!file && (
              <>
                <p className="mb-4">
                  Esta sección permite iniciar una solicitud al padrón
                  utilizando la imagen de un pasaporte. Para continuar, adjunte
                  una fotografía o archivo PDF del pasaporte en el recuadro
                  inferior.
                  <GuiaPasaporteModal />
                </p>
                <div
                  {...getRootProps()}
                  className={`p-5 text-center ${
                    isDragActive
                      ? "border-primary alert-primary shadow-lg"
                      : "border-secondary bg-light"
                  }`}
                  style={{
                    cursor: "pointer",
                    border: "2px dashed",
                    borderRadius: "8px",
                    transition: "all 0.3s ease",
                    transform: isDragActive ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  <input {...getInputProps()} />

                  <i
                    className={`fas fa-cloud-upload-alt mb-3 ${
                      isDragActive ? "text-primary" : "text-muted"
                    }`}
                    style={{ fontSize: "3rem" }}
                  ></i>

                  {isDragActive ? (
                    <p className="text-primary font-weight-bold lead m-0">
                      Suelte el archivo aquí...
                    </p>
                  ) : (
                    <p className="m-0 lead">
                      Arrastre o haga clic para adjuntar una imagen o PDF
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-md-7 border-right d-flex flex-column text-center">
                {/* Columna izquierda: preview */}
                {file && file.type.includes("image") && (
                  <>
                    <div className="mb-2">
                      <button
                        className="btn btn-xs btn-secondary mr-2"
                        onClick={zoomIn}
                      >
                        Zoom +
                      </button>
                      <button
                        className="btn btn-xs btn-secondary mr-2"
                        onClick={zoomOut}
                      >
                        Zoom -
                      </button>
                      <button
                        className="btn btn-xs btn-secondary"
                        onClick={resetearZoom}
                      >
                        Restablecer
                      </button>
                    </div>

                    <div
                      ref={viewerRef}
                      className="flex-grow-1 overflow-auto bg-light d-flex justify-content-center align-items-start rounded"
                    >
                      <img
                        src={preview}
                        alt="preview"
                        className="img-fluid h-100"
                        style={{
                          objectFit: "contain",
                          transform: `scale(${scale})`,
                          transformOrigin: "top left",
                          maxWidth: "none",
                        }}
                      />
                    </div>
                  </>
                )}

                {file && file.type.includes("pdf") && (
                  <iframe
                    src={preview}
                    title="pdf-preview"
                    className="w-100 h-100"
                  />
                )}
              </div>

              <div className="col-md-5">
                {/* Datos de persona */}
                {datosSolicitud && (
                  <>
                    <h4 className="font-weight-bold">Datos de la Solicitud</h4>
                    <div
                      className="alert alert-warning d-flex align-items-center p-2"
                      role="alert"
                    >
                      <span>
                        <strong>Atención: </strong>Por favor, valide los datos
                        antes de ingresar la solicitud.
                      </span>
                    </div>
                    <form>
                      <div className="form-group">
                        <label htmlFor="tipoSolicitud">Tipo de Solicitud</label>
                        <input
                          type="text"
                          className={`form-control border-left-${
                            datosSolicitud.tipoSolicitud === 1
                              ? "primary"
                              : "warning"
                          }`}
                          id="tipoSolicitud"
                          name="tipoSolicitud"
                          value={
                            datosSolicitud.tipoSolicitud === 1
                              ? "Inclusión al padrón"
                              : datosSolicitud.tipoSolicitud === 2
                              ? "Modificación al padrón"
                              : ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="identificacionPersona">
                          Identificación
                        </label>
                        <InputTextoPadron
                          type="text"
                          className="form-control"
                          id="identificacionPersona"
                          name="identificacionPersona"
                          value={datosSolicitud.identificacionPersona || ""}
                          permiteEspacios={false}
                          onChange={handleChange}
                        />
                        {!esIdentificacionValida && (
                          <div className="invalid-feedback d-block">
                            No se pudo validar la identificación. Por favor
                            intente de nuevo.
                            <button
                              type="button"
                              className="btn btn-link btn-sm ml-2 p-0 align-baseline"
                              onClick={() =>
                                obtenerPersona(
                                  datosSolicitud.identificacionPersona
                                )
                              }
                            >
                              Validar Identificación
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="nombreORazonSocial">Nombre</label>
                        <InputTextoPadron
                          type="text"
                          className={`form-control ${
                            cambios.nombreORazonSocial ? "border-warning" : ""
                          }`}
                          id="nombreORazonSocial"
                          name="nombreORazonSocial"
                          value={datosSolicitud.nombreORazonSocial || ""}
                          maxLength={255}
                          onChange={handleChange}
                        />
                        {personaEnBD &&
                          renderOriginal(
                            "nombreORazonSocial",
                            personaEnBD?.nombreORazonSocial,
                            personaEnBD?.nombreORazonSocial
                          )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="primerApellido">Primer Apellido</label>
                        <InputTextoPadron
                          type="text"
                          className={`form-control ${
                            cambios.primerApellido ? "border-warning" : ""
                          }`}
                          id="primerApellido"
                          name="primerApellido"
                          value={datosSolicitud.primerApellido || ""}
                          onChange={handleChange}
                        />
                        {personaEnBD &&
                          renderOriginal(
                            "primerApellido",
                            personaEnBD?.primerApellido,
                            personaEnBD?.primerApellido
                          )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="segundoApellido">
                          Segundo Apellido
                        </label>
                        <InputTextoPadron
                          type="text"
                          className={`form-control ${
                            cambios.segundoApellido ? "border-warning" : ""
                          }`}
                          id="segundoApellido"
                          name="segundoApellido"
                          value={datosSolicitud.segundoApellido || ""}
                          onChange={handleChange}
                        />
                        {personaEnBD &&
                          renderOriginal(
                            "segundoApellido",
                            personaEnBD?.segundoApellido,
                            personaEnBD?.segundoApellido
                          )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="fechaConstitucionONacimiento">
                          Fecha de Nacimiento
                        </label>
                        <br />
                        <DatePicker
                          id="fechaConstitucionONacimiento"
                          locale="es"
                          selected={
                            datosSolicitud?.fechaConstitucionONacimiento
                          }
                          onChange={(fecha) =>
                            handleChange({
                              target: {
                                name: "fechaConstitucionONacimiento",
                                value: fecha,
                              },
                            })
                          }
                          dateFormat="dd/MM/yyyy"
                          className={`form-control ${
                            cambios.fechaConstitucionONacimiento
                              ? "border-warning"
                              : ""
                          }`}
                        />
                        {personaEnBD &&
                          renderOriginal(
                            "fechaConstitucionONacimiento",
                            format(
                              new Date(
                                personaEnBD?.fechaConstitucionONacimiento
                              ),
                              "dd/MM/yyyy"
                            ),
                            personaEnBD?.fechaConstitucionONacimiento
                          )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="paisRegistroONacimiento">País</label>
                        <PaisesDropdown
                          id="paisRegistroONacimiento"
                          name="paisRegistroONacimiento"
                          value={datosSolicitud?.paisRegistroONacimiento || ""}
                          onChange={handleChange}
                          className={`form-control ${
                            cambios.paisRegistroONacimiento
                              ? "border-warning"
                              : ""
                          }`}
                        />
                        {personaEnBD &&
                          renderOriginal(
                            "paisRegistroONacimiento",
                            personaEnBD?.paisRegistroONacimiento?.nombrePais,
                            personaEnBD?.paisRegistroONacimiento?.codigoPais
                          )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="genero">Género</label>
                        <GenerosDropdown
                          id="genero"
                          name="genero"
                          className={`form-control ${
                            cambios.genero ? "border-warning" : ""
                          }`}
                          value={datosSolicitud.genero || ""}
                          onChange={handleChange}
                        />
                        {personaEnBD &&
                          renderOriginal(
                            "genero",
                            personaEnBD?.descripcionGenero,
                            personaEnBD?.genero
                          )}
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SolicitudPasaporte;
